import { getRootPosition, LAYER_TYPES } from '../../../../../lib';
import { VERTICAL_BUFFER_RATIO } from './constants';

/**
 * @param {Root} root
 * @returns {Boolean}
 */
const isEmptyTextRoot = ({ layers, layerType }) =>
    layerType === LAYER_TYPES.TEXT && !layers.some(({ children }) => !!children);

/**
 * Creates for each root, its corresponding vertical changes effect map for any other root
 * @param {Root[]} roots
 * @returns {Object}
 */
const createVerticalMap = (roots) =>
    roots.reduce((map, { layerId, ...root }) => {
        // No layers can affect an empty text field, and an empty text root cannot affect others.
        if (isEmptyTextRoot(root)) {
            map[layerId] = {};
            return map;
        }

        const { top: constrainedTop, height: constrainedHeight } = getRootPosition(root);

        const otherRoots = roots.filter((root) => root.layerId !== layerId);

        map[layerId] = otherRoots.reduce((rootEffects, otherRoot) => {
            const { top, height } = getRootPosition(otherRoot);

            const relativeBuffer = height * VERTICAL_BUFFER_RATIO;

            // Effected items will be those who are above / bellow the trigger root (in buffer range)
            const isAbove = top + height <= constrainedTop + relativeBuffer;
            const isBelow = top >= constrainedTop + constrainedHeight - relativeBuffer;

            const isEffected = isAbove || isBelow;
            if (!isEffected) {
                return rootEffects;
            }

            rootEffects[otherRoot.layerId] = { effected: isEffected, above: isAbove, below: isBelow };
            return rootEffects;
        }, {});

        return map;
    }, {});

/**
 * Returns an array of vertical driven changes for a given variation update,
 * @param update - The current variation root / layer update
 * @param roots - The current updated variation roots
 * @param originalRoots - The original variation roots
 * @returns {[Object]}
 */
const getRootsVerticalChanges = (update, roots, originalRoots) => {
    const updatedRootId = update.root && update.root.layerId;

    if (!updatedRootId) {
        return [];
    }

    const triggerRoot = roots.find(({ layerId }) => layerId === updatedRootId);
    const currentHeight = parseFloat(triggerRoot.style.height);
    const updateHeight = parseFloat(update.root.style.height);

    const grow = updateHeight > currentHeight;
    const heightDelta = Math.abs(currentHeight - updateHeight) * (grow ? 1 : -1);

    if (heightDelta) {
        const verticalEffectMap = createVerticalMap(originalRoots);

        const triggerRootEffects = verticalEffectMap[triggerRoot.layerId];

        return roots.reduce((verticalChanges, { layerId, ...root }) => {
            const { top } = getRootPosition(root);

            const isTrigger = layerId === triggerRoot.layerId;
            const { effected = false, above, below } = triggerRootEffects[layerId] || {};

            if (!effected && !isTrigger) {
                return verticalChanges;
            }

            let newTop = 0;
            if (isTrigger) {
                // Trigger root will al ways be effected by half of the delta
                newTop = top - heightDelta / 2;
            } else if (above) {
                // Upper elements will be effected as the trigger direction by full delta
                newTop = top - heightDelta;
            } else if (below) {
                // Bottom elements will use opposite of the direction of the delta, effected by half of delta
                newTop = top + heightDelta / 2;
            }

            const verticalChange = { root: { layerId, y: newTop, style: { top: `${newTop}px` } } };

            return [...verticalChanges, verticalChange];
        }, []);
    }

    return [];
};

export default getRootsVerticalChanges;
